/**
 * Trial plan labelling.
 *
 * A trial plan key (e.g. BASIC_TRIAL) is a valid plan state, but it is NOT a
 * key in a partner's priced plan family — the family only holds payable plans
 * (BASIC_MONTHLY, PRO_MONTHLY, and the yearly variants). Any UI that resolves
 * a plan by looking it up in the family therefore finds nothing and falls back
 * to a placeholder such as "NA", which reads as "this project has no plan"
 * even though the project is on a perfectly valid trial.
 *
 * That is what happened in PTR-40: a partner tested Embedded Signup, saw
 * "NA" against the plan, and reported it as a provisioning failure. The
 * project was mid-trial with BASIC_MONTHLY correctly scheduled.
 *
 * The trial label matches the one already used for billing activity messages
 * in billingsHelper ("Basic Trial").
 */

export const isTrialPlan = (plan) =>
  typeof plan === "string" && /_TRIAL$/i.test(plan);

/** BASIC_TRIAL -> "Basic Trial", PRO_MONTHLY -> "Pro Monthly" */
export const humanisePlanKey = (plan) => {
  if (typeof plan !== "string" || !plan) return "";
  return plan
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 * Decorate the coarse plan tier shown on the project card so a trial is never
 * indistinguishable from "no plan".
 *  ("Basic", "BASIC_TRIAL")  -> "Basic (Trial)"
 *  ("NA",    "BASIC_TRIAL")  -> "Basic Trial"
 *  ("Basic", "BASIC_MONTHLY")-> "Basic"
 */
export const decorateTrialType = (type, plan) => {
  if (!isTrialPlan(plan)) return type;
  return !type || type === "NA" ? humanisePlanKey(plan) : `${type} (Trial)`;
};
